import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CalendarRange,
  Check,
  CheckCircle2,
  Layers3,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  UserMinus,
  UsersRound,
} from 'lucide-react';
import MainLayout from '../../../app/layouts/MainLayout';
import { Button, cn } from '../../../components/ui/Button';
import Avatar from '../../../components/ui/Avatar';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import FullScreenModal from '../../../components/ui/Modal';
import Toast, { ToastMessage } from '../../../components/ui/Toast';
import { classes as classSeed } from '../../../data/modules';
import { Trimester, useTrimesters } from '../context/TrimestersContext';
import { getPersonPhotoUrl } from '../../../utils/helpers/avatar';
import GeneralSettingsPanel from '../components/GeneralSettingsPanel';
import { useMagazines, useSegments } from '../hooks';
import { Segment } from '../types/trimestersSettings.types';

type FormMode = 'create' | 'edit';
type CreateStep = 1 | 2 | 3;
type PageSection = 'trimesters' | 'magazines';
type ReuseMode = 'all' | 'selected' | 'none';

interface ClassReuseSettings {
  includeClass: boolean;
  segmentId: string;
  includeTeachers: boolean;
  includeStudents: boolean;
  selectedClassIds?: string[];
}

interface FormState {
  year: string;
  number: string;
  startDate: string;
  endDate: string;
  replicateFromId: string;
  excludedStudentIds: string[];
  classReuseMode: ReuseMode;
  studentReuseMode: ReuseMode;
}

const emptyForm: FormState = {
  year: '2026',
  number: '1',
  startDate: '',
  endDate: '',
  replicateFromId: '',
  excludedStudentIds: [],
  classReuseMode: 'all',
  studentReuseMode: 'all',
};

const formatBrDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getSuggestedTrimesterDates = (year: number, number: number) => {
  if (!year || number < 1 || number > 4) {
    return null;
  }

  const startMonth = (number - 1) * 3;
  const endMonth = startMonth + 2;
  const firstDay = new Date(year, startMonth, 1);
  const lastDay = new Date(year, endMonth + 1, 0);
  const daysUntilSunday = (7 - firstDay.getDay()) % 7;
  const daysSinceSunday = lastDay.getDay();

  const startDate = new Date(year, startMonth, 1 + daysUntilSunday);
  const endDate = new Date(year, endMonth, lastDay.getDate() - daysSinceSunday);

  return {
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(endDate),
  };
};

const UNASSIGNED_KEY = '__unassigned__';
const UNASSIGNED_LABEL = 'Sem classe vinculada';

const normalizeSegmentText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const segmentKeywordGroups = [
  ['prim'],
  ['adolesc'],
  ['obreiro'],
  ['adult'],
  ['bercario'],
];

const segmentByClassCategory = (category: string, segments: Segment[]) => {
  const normalizedCategory = normalizeSegmentText(category);
  const fallbackSegmentId = segments[0]?.id ?? '';
  const directMatch = segments.find((segment) => {
    const normalizedSegment = normalizeSegmentText(segment.name);
    return normalizedCategory.includes(normalizedSegment) || normalizedSegment.includes(normalizedCategory);
  });

  if (directMatch) {
    return directMatch.id;
  }

  const keywordGroup = segmentKeywordGroups.find((keywords) =>
    keywords.some((keyword) => normalizedCategory.includes(keyword))
  );
  const keywordMatch = keywordGroup
    ? segments.find((segment) =>
        keywordGroup.some((keyword) => normalizeSegmentText(segment.name).includes(keyword))
      )
    : null;

  return keywordMatch?.id ?? fallbackSegmentId;
};

const buildClassReuseSettings = (segments: Segment[]) =>
  classSeed.reduce<Record<string, ClassReuseSettings>>((accumulator, classItem) => {
    accumulator[classItem.id] = {
      includeClass: true,
      segmentId: segmentByClassCategory(classItem.category, segments),
      includeTeachers: true,
      includeStudents: true,
    };

    return accumulator;
  }, {});

const TrimestersPage: React.FC = () => {
  const navigate = useNavigate();
  const { segments } = useSegments();
  const { trimesterId: urlTrimesterId } = useParams<{ trimesterId?: string }>();
  const {
    trimesters,
    currentTrimesterId,
    setCurrentTrimesterId,
    createTrimester,
    updateTrimester,
    deleteTrimester,
    isLoadingTrimesters,
    trimestersError,
    reloadTrimesters,
    studentsByTrimester,
    removeReplicatedStudent,
    sourceTrimesterClasses,
    isLoadingSourceClasses,
    loadSourceTrimesterClasses,
  } = useTrimesters();
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [formValues, setFormValues] = useState<FormState>(emptyForm);
  const [createStep, setCreateStep] = useState<CreateStep>(1);
  const [classReuseSettings, setClassReuseSettings] = useState<Record<string, ClassReuseSettings>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [trimesterToDelete, setTrimesterToDelete] = useState<Trimester | null>(null);
  const [managedTrimesterId, setManagedTrimesterId] = useState<string | null>(null);
  const [pageSection, setPageSection] = useState<PageSection>('trimesters');
  const [magazinesTrimesterId, setMagazinesTrimesterId] = useState<string>(currentTrimesterId);
  const [isSaving, setIsSaving] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [operationNotice, setOperationNotice] = useState<ToastMessage | null>(null);
  const { getMagazineForSegment } = useMagazines(pageSection === 'magazines' ? magazinesTrimesterId : undefined);

  useEffect(() => {
    if (formValues.replicateFromId) {
      void loadSourceTrimesterClasses(formValues.replicateFromId);
    }
  }, [formValues.replicateFromId, loadSourceTrimesterClasses]);

  // Inicializar selectedClassIds quando as turmas são carregadas
  useEffect(() => {
    if (sourceTrimesterClasses.length > 0) {
      setClassReuseSettings((current) => {
        const updated = { ...current };
        
        // Agrupar classes por segmento
        const groups = new Map<string, string[]>();
        sourceTrimesterClasses.forEach((classItem) => {
          const segmentName = segments.find((s) => s.id === classItem.segmentId)?.name ?? 'Segmento desconhecido';
          const existing = groups.get(segmentName) ?? [];
          groups.set(segmentName, [...existing, classItem.id]);
        });

        // Inicializar selectedClassIds para cada grupo
        groups.forEach((classIds, segmentName) => {
          if (!updated[segmentName]) {
            updated[segmentName] = {
              includeClass: true,
              segmentId: segments.find((s) => s.name === segmentName)?.id ?? '',
              includeTeachers: true,
              includeStudents: true,
              selectedClassIds: classIds,
            };
          } else {
            updated[segmentName] = {
              ...updated[segmentName],
              selectedClassIds: classIds,
            };
          }
        });

        return updated;
      });
    }
  }, [sourceTrimesterClasses, segments]);

  useEffect(() => {
    if (urlTrimesterId) {
      const trimesterExists = trimesters.some((item) => item.id === urlTrimesterId);

      if (!isLoadingTrimesters && trimesters.length > 0 && !trimesterExists) {
        const fallbackTrimesterId = currentTrimesterId || trimesters[0]?.id;

        if (fallbackTrimesterId) {
          setMagazinesTrimesterId(fallbackTrimesterId);
          navigate(`/trimestres/${fallbackTrimesterId}/revistas`, { replace: true });
          return;
        }
      }

      setPageSection('magazines');
      setMagazinesTrimesterId(urlTrimesterId);
      if (trimesterExists && urlTrimesterId !== currentTrimesterId) {
        setCurrentTrimesterId(urlTrimesterId);
      }
      return;
    }

    if (pageSection === 'magazines') {
      setMagazinesTrimesterId(currentTrimesterId);
    } else {
      setPageSection('trimesters');
    }
  }, [
    urlTrimesterId,
    currentTrimesterId,
    pageSection,
    trimesters,
    isLoadingTrimesters,
    navigate,
    setCurrentTrimesterId,
  ]);

  const managedTrimester = managedTrimesterId ? trimesters.find((item) => item.id === managedTrimesterId) ?? null : null;
  const magazinesTrimester = trimesters.find((item) => item.id === magazinesTrimesterId) ?? null;
  const managedStudents = useMemo(
    () => (managedTrimesterId ? studentsByTrimester[managedTrimesterId] ?? [] : []),
    [managedTrimesterId, studentsByTrimester]
  );

  const sortedTrimesters = useMemo(
    () => [...trimesters].sort((a, b) => (a.year === b.year ? b.number - a.number : b.year - a.year)),
    [trimesters]
  );

  const openCreate = () => {
    const previous = sortedTrimesters[0];
    const suggestedYear = previous ? previous.year + (previous.number === 4 ? 1 : 0) : 2026;
    const suggestedNumber = previous ? (previous.number === 4 ? 1 : previous.number + 1) : 1;
    const suggestedDates = getSuggestedTrimesterDates(suggestedYear, suggestedNumber);

    setOperationError(null);
    setFormMode('create');
    setCreateStep(1);
    setEditingId(null);
    setFormValues({
      ...emptyForm,
      year: String(suggestedYear),
      number: String(suggestedNumber),
      startDate: suggestedDates?.startDate ?? '',
      endDate: suggestedDates?.endDate ?? '',
      replicateFromId: previous?.id ?? '',
      classReuseMode: 'all',
      studentReuseMode: 'all',
    });
    setClassReuseSettings(buildClassReuseSettings(segments));
  };

  const openEdit = (trimester: Trimester) => {
    setOperationError(null);
    setFormMode('edit');
    setCreateStep(1);
    setEditingId(trimester.id);
    setFormValues({
      year: String(trimester.year),
      number: String(trimester.number),
      startDate: trimester.startDate,
      endDate: trimester.endDate,
      replicateFromId: '',
      excludedStudentIds: [],
      classReuseMode: 'all',
      studentReuseMode: 'all',
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setCreateStep(1);
    setEditingId(null);
    setFormValues(emptyForm);
    setClassReuseSettings({});
    setOperationError(null);
  };

  const canContinueCreateStep = () => {
    const year = Number(formValues.year);
    const number = Number(formValues.number);

    if (createStep === 1) {
      return Boolean(formValues.startDate && formValues.endDate && year && number);
    }

    return true;
  };

  const updateTrimesterPeriod = (updates: Pick<FormState, 'year'> | Pick<FormState, 'number'>) => {
    setFormValues((current) => {
      const next = { ...current, ...updates };
      const year = Number(next.year);
      const number = Number(next.number);
      const suggestedDates = getSuggestedTrimesterDates(year, number);

      if (!suggestedDates) {
        return next;
      }

      return {
        ...next,
        startDate: suggestedDates.startDate,
        endDate: suggestedDates.endDate,
      };
    });
  };

  const handleCreateFooterAction = () => {
    if (createStep < 3) {
      if (!canContinueCreateStep()) return;
      setCreateStep((current) => (current === 1 ? 2 : 3));
      return;
    }

    void handleSubmitForm();
  };

  const handleSubmitForm = async () => {
    const year = Number(formValues.year);
    const number = Number(formValues.number);

    if (!formValues.startDate || !formValues.endDate || !year || !number) {
      return;
    }

    setIsSaving(true);
    setOperationError(null);

    try {
      if (formMode === 'create') {
        await createTrimester({
          year,
          number: number as 1 | 2 | 3 | 4,
          startDate: formValues.startDate,
          endDate: formValues.endDate,
          replicateFromId: formValues.replicateFromId || undefined,
          excludedStudentIds:
            formValues.replicateFromId && formValues.studentReuseMode === 'selected'
              ? formValues.excludedStudentIds
              : undefined,
          replicationOptions: formValues.replicateFromId ? {
            selectedClassIds: formValues.classReuseMode === 'all'
              ? undefined
              : formValues.classReuseMode === 'none'
              ? []
              : Object.values(classReuseSettings).flatMap((setting) => setting.selectedClassIds ?? []),
            includeTeachers: Object.values(classReuseSettings).some(setting => setting.includeTeachers),
            includeStudents:
              formValues.studentReuseMode === 'all'
                ? true
                : formValues.studentReuseMode === 'none'
                ? false
                : Object.values(classReuseSettings).some((setting) => setting.includeStudents),
          } : undefined,
        });
      } else if (formMode === 'edit' && editingId) {
        await updateTrimester(editingId, {
          year,
          number: number as 1 | 2 | 3 | 4,
          startDate: formValues.startDate,
          endDate: formValues.endDate,
        });
      }

      closeForm();
    } catch (error) {
      setOperationError(error instanceof Error ? error.message : 'Nao foi possivel salvar o trimestre.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!trimesterToDelete) return;

    setIsSaving(true);
    setOperationError(null);

    try {
      const deletedTrimesterLabel = trimesterToDelete.label;
      await deleteTrimester(trimesterToDelete.id);
      setTrimesterToDelete(null);
      setOperationNotice({
        type: 'success',
        message: `${deletedTrimesterLabel} removido com sucesso.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel remover o trimestre.';
      setOperationError(message);
      setOperationNotice({
        type: 'error',
        message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const replicationSource = useMemo(
    () => (formValues.replicateFromId ? studentsByTrimester[formValues.replicateFromId] ?? [] : []),
    [formValues.replicateFromId, studentsByTrimester]
  );

  const replicationGroups = useMemo(() => {
    if (!formValues.replicateFromId || isLoadingSourceClasses) {
      return [];
    }

    // Agrupar classes por segmento
    const groups = new Map<string, { title: string; classes: typeof sourceTrimesterClasses }>();

    sourceTrimesterClasses.forEach((classItem) => {
      const segment = segments.find((s) => s.id === classItem.segmentId);
      const segmentName = segment?.name ?? 'Segmento desconhecido';
      
      const existing = groups.get(segmentName);
      if (existing) {
        existing.classes.push(classItem);
      } else {
        groups.set(segmentName, { title: segmentName, classes: [classItem] });
      }
    });

    return Array.from(groups.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  }, [replicationSource]);

  const managedGroups = useMemo(() => {
    const groups = new Map<string, { title: string; students: typeof managedStudents }>();

    managedStudents.forEach((student) => {
      const key = student.currentClassId ?? UNASSIGNED_KEY;
      const title = student.currentClassId
        ? classSeed.find((item) => item.id === student.currentClassId)?.title ?? 'Classe removida'
        : UNASSIGNED_LABEL;

      const existing = groups.get(key);
      if (existing) {
        existing.students.push(student);
      } else {
        groups.set(key, { title, students: [student] });
      }
    });

    return Array.from(groups.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => {
        if (a.key === UNASSIGNED_KEY) return 1;
        if (b.key === UNASSIGNED_KEY) return -1;
        return a.title.localeCompare(b.title, 'pt-BR');
      });
  }, [managedStudents]);

  const toggleExcluded = (studentId: string) => {
    setFormValues((current) => {
      const excluded = new Set(current.excludedStudentIds);
      if (excluded.has(studentId)) {
        excluded.delete(studentId);
      } else {
        excluded.add(studentId);
      }
      return { ...current, excludedStudentIds: Array.from(excluded) };
    });
  };

  const setGroupStudentExclusion = (studentIds: string[], excluded: boolean) => {
    setFormValues((current) => {
      const excludedIds = new Set(current.excludedStudentIds);

      studentIds.forEach((id) => {
        if (excluded) {
          excludedIds.add(id);
        } else {
          excludedIds.delete(id);
        }
      });

      return { ...current, excludedStudentIds: Array.from(excludedIds) };
    });
  };

  const updateClassReuseSetting = (classId: string, updates: Partial<ClassReuseSettings>) => {
    setClassReuseSettings((current) => ({
      ...current,
      [classId]: {
        ...(current[classId] ?? {
          includeClass: true,
          segmentId: segments[0]?.id ?? '',
          includeTeachers: true,
          includeStudents: true,
        }),
        ...updates,
      },
    }));
  };

  const includedClassGroups = useMemo(
    () => {
      if (formValues.classReuseMode === 'none') {
        return [];
      }

      if (formValues.classReuseMode === 'all') {
        return replicationGroups.filter((group) => group.key !== UNASSIGNED_KEY);
      }

      return replicationGroups.filter((group) => {
        if (group.key === UNASSIGNED_KEY) return false;
        return classReuseSettings[group.key]?.includeClass ?? true;
      });
    },
    [classReuseSettings, formValues.classReuseMode, replicationGroups]
  );

  return (
    <MainLayout>
      <Toast notice={operationNotice} onClose={() => setOperationNotice(null)} />

      <div className="space-y-6">
        <button
          type="button"
          onClick={() => navigate('/mais')}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft size={18} /> Voltar
        </button>

        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {pageSection === 'trimesters'
                ? 'Gestão de trimestres'
                : `Revistas do trimestre${magazinesTrimester ? `: ${magazinesTrimester.label}` : ''}`}
            </h2>
            <p className="text-sm text-gray-500">
              {pageSection === 'trimesters'
                ? 'Crie, edite e reaproveite trimestres da EBD.'
                : 'Gerencie as revistas do trimestre selecionado.'}
            </p>
          </div>
          {pageSection === 'trimesters' ? (
            <Button className="h-12 gap-2 px-4" onClick={openCreate} disabled={isLoadingTrimesters}>
              <Plus size={18} />
              Novo trimestre
            </Button>
          ) : (
            <Button variant="ghost" className="h-12 border border-gray-200 px-4" onClick={() => navigate('/trimestres')}>
              Voltar
            </Button>
          )}
        </div>

        {pageSection === 'trimesters' ? (
          <>
            {(trimestersError || operationError) && (
              <Card className="border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-900">{operationError ?? trimestersError}</p>
                  </div>
                  {trimestersError && (
                    <button
                      type="button"
                      onClick={() => reloadTrimesters()}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-800"
                    >
                      <RefreshCw size={12} /> Recarregar
                    </button>
                  )}
                </div>
              </Card>
            )}

            {isLoadingTrimesters && (
              <Card className="border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <RefreshCw size={16} className="animate-spin text-primary" />
                  Carregando trimestres...
                </div>
              </Card>
            )}

            <div className="space-y-3">
              {sortedTrimesters.map((trimester) => {
                const isCurrent = trimester.id === currentTrimesterId;
                const isMagazineSelected = trimester.id === magazinesTrimesterId;
                const studentCount = (studentsByTrimester[trimester.id] ?? []).length;

                return (
                  <Card
                    key={trimester.id}
                    className={cn(
                      'space-y-3 border-slate-200 p-4',
                      isMagazineSelected && 'border-primary ring-2 ring-primary/10'
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <CalendarRange size={16} className="text-primary" />
                          <h3 className="text-base font-bold text-slate-900">{trimester.label}</h3>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatBrDate(trimester.startDate)} até {formatBrDate(trimester.endDate)}
                        </p>
                        <p className="mt-1 text-xs text-slate-600">
                          {studentCount} {studentCount === 1 ? 'aluno vinculado' : 'alunos vinculados'}
                        </p>
                      </div>
                      {isCurrent ? (
                        <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                          <Check size={12} /> Ativo
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCurrentTrimesterId(trimester.id)}
                          className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700"
                        >
                          Tornar ativo
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/trimestres/${trimester.id}/revistas`)}
                        className={cn(
                          'inline-flex h-11 items-center justify-center rounded-xl px-3 text-xs font-semibold',
                          isMagazineSelected ? 'bg-primary text-white' : 'bg-amber-50 text-amber-700'
                        )}
                      >
                        Revistas
                      </button>
                      <button
                        type="button"
                        onClick={() => setManagedTrimesterId(trimester.id)}
                        className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-50 px-3 text-xs font-semibold text-slate-700"
                      >
                        Alunos
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(trimester)}
                        className="inline-flex h-11 items-center justify-center gap-1 rounded-xl bg-sky-50 px-3 text-xs font-semibold text-sky-700"
                      >
                        <Pencil size={12} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setTrimesterToDelete(trimester)}
                        disabled={sortedTrimesters.length <= 1}
                        className={cn(
                          'inline-flex h-11 items-center justify-center gap-1 rounded-xl px-3 text-xs font-semibold',
                          sortedTrimesters.length <= 1
                            ? 'cursor-not-allowed bg-slate-50 text-slate-400'
                            : 'bg-rose-50 text-rose-700'
                        )}
                      >
                        <Trash2 size={12} /> Remover
                      </button>
                    </div>
                  </Card>
                );
              })}

              {sortedTrimesters.length === 0 && (
                <Card className="border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                  <p className="text-sm font-semibold text-slate-700">Nenhum trimestre cadastrado.</p>
                  <p className="mt-1 text-sm text-slate-500">Crie o primeiro trimestre para começar.</p>
                </Card>
              )}
            </div>
          </>
        ) : (
          <section className="space-y-4 border-t border-slate-200 pt-5">
            <GeneralSettingsPanel
              trimesters={sortedTrimesters}
              currentTrimesterId={magazinesTrimesterId}
              mode="magazines"
              showHeader={false}
            />
          </section>
        )}
      </div>

      <FullScreenModal
        open={formMode !== null}
        onClose={closeForm}
        title={formMode === 'edit' ? 'Editar trimestre' : 'Novo trimestre'}
        subtitle="Cadastre o trimestre e defina os reaproveitamentos no mesmo fluxo."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              disabled={isSaving}
              onClick={() => {
                if (formMode === 'create' && createStep > 1) {
                  setCreateStep((current) => (current === 3 ? 2 : 1));
                  return;
                }

                closeForm();
              }}
            >
              {formMode === 'create' && createStep > 1 ? 'Voltar' : 'Cancelar'}
            </Button>
            <Button
              className="h-12"
              onClick={formMode === 'create' ? handleCreateFooterAction : () => void handleSubmitForm()}
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : formMode === 'edit' ? 'Salvar alterações' : createStep === 3 ? 'Criar trimestre' : 'Continuar'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {operationError && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700" />
              {operationError}
            </div>
          )}

          {formMode === 'create' && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { step: 1, label: 'Dados' },
                { step: 2, label: 'Reuso' },
                { step: 3, label: 'Revisao' },
              ].map((item) => (
                <button
                  key={item.step}
                  type="button"
                  onClick={() => {
                    if (item.step === 1 || canContinueCreateStep()) {
                      setCreateStep(item.step as CreateStep);
                    }
                  }}
                  className={cn(
                    'h-10 rounded-xl text-xs font-bold transition-colors',
                    createStep >= item.step ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {(formMode === 'edit' || createStep === 1) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Ano"
                  type="number"
                  value={formValues.year}
                  onChange={(event) => updateTrimesterPeriod({ year: event.target.value })}
                />
                <div className="space-y-1.5">
                  <label className="ml-1 text-sm font-medium text-gray-700">Trimestre</label>
                  <select
                    value={formValues.number}
                    onChange={(event) => updateTrimesterPeriod({ number: event.target.value })}
                    className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="1">1º trimestre</option>
                    <option value="2">2º trimestre</option>
                    <option value="3">3º trimestre</option>
                    <option value="4">4º trimestre</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Início"
                  type="date"
                  value={formValues.startDate}
                  onChange={(event) => setFormValues((current) => ({ ...current, startDate: event.target.value }))}
                />
                <Input
                  label="Término"
                  type="date"
                  value={formValues.endDate}
                  onChange={(event) => setFormValues((current) => ({ ...current, endDate: event.target.value }))}
                />
              </div>
            </>
          )}

          {formMode === 'create' && createStep === 2 && (
            <div className="space-y-4">
              <Card className="border-slate-200 bg-slate-50 p-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Reaproveitamentos do novo trimestre</p>
                    <p className="text-xs text-gray-500">
                      Escolha a origem uma vez e ajuste turmas, alunos, professores, segmentos, revistas e lições abaixo.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="ml-1 text-xs font-medium text-gray-700">Trimestre anterior</label>
                    <select
                      value={formValues.replicateFromId}
                      onChange={(event) =>
                        setFormValues((current) => ({
                          ...current,
                          replicateFromId: event.target.value,
                          excludedStudentIds: [],
                          classReuseMode: 'all',
                          studentReuseMode: 'all',
                        }))
                      }
                      className="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="">Começar sem reaproveitar</option>
                      {sortedTrimesters.map((trimester) => (
                        <option key={trimester.id} value={trimester.id}>
                          {trimester.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formValues.replicateFromId && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="ml-1 text-xs font-medium text-gray-700">Turmas</label>
                        <select
                          value={formValues.classReuseMode}
                          onChange={(event) =>
                            setFormValues((current) => ({
                              ...current,
                              classReuseMode: event.target.value as ReuseMode,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="all">Reaproveitar todas</option>
                          <option value="selected">Selecionar turmas</option>
                          <option value="none">Nao reaproveitar turmas</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <label className="ml-1 text-xs font-medium text-gray-700">Alunos</label>
                        <select
                          value={formValues.studentReuseMode}
                          onChange={(event) =>
                            setFormValues((current) => ({
                              ...current,
                              studentReuseMode: event.target.value as ReuseMode,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="all">Reaproveitar todos</option>
                          <option value="none">Nao reaproveitar alunos</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {formValues.replicateFromId &&
                formValues.classReuseMode !== 'none' &&
                replicationGroups.length > 0 &&
                !isLoadingSourceClasses && (
                <div className="space-y-3">
                  {replicationGroups.map((group) => {
                    const reuseSetting = classReuseSettings[group.key] ?? {
                      includeClass: true,
                      segmentId: segments[0]?.id ?? '',
                      includeTeachers: true,
                      includeStudents: true,
                    };
                    const isSegmentIncluded = reuseSetting.includeClass;
                    const magazine = reuseSetting.segmentId
                      ? getMagazineForSegment(reuseSetting.segmentId, formValues.replicateFromId)
                      : null;

                    return (
                      <div key={group.key} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-900">{group.title}</p>
                            <p className="text-xs text-gray-500">
                              {group.classes.length} turmas encontradas
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              updateClassReuseSetting(group.key, {
                                includeClass: !isSegmentIncluded,
                                includeStudents: !isSegmentIncluded,
                              });
                            }}
                            className={cn(
                              'rounded-full px-3 py-1.5 text-[11px] font-semibold',
                              isSegmentIncluded ? 'bg-primary-light text-primary' : 'bg-slate-100 text-slate-600'
                            )}
                          >
                            {isSegmentIncluded ? 'Remover segmento' : 'Incluir segmento'}
                          </button>
                        </div>

                        {isSegmentIncluded && (
                          <div className="grid gap-3">
                            <div className="space-y-1.5">
                              <label className="ml-1 text-xs font-medium text-gray-700">Segmento das turmas</label>
                              <select
                                value={reuseSetting.segmentId}
                                onChange={(event) =>
                                  updateClassReuseSetting(group.key, {
                                    segmentId: event.target.value,
                                  })
                                }
                                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                              >
                                {segments.map((segment) => (
                                  <option key={segment.id} value={segment.id}>
                                    {segment.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                              <p className="flex items-center gap-2 text-xs font-bold text-slate-700">
                                <BookOpen size={14} className="text-primary" />
                                Revista e lições
                              </p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">
                                {magazine?.title ?? 'Nenhuma revista encontrada para este segmento'}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {magazine
                                  ? `${magazine.lessons.length} lições vinculadas automaticamente`
                                  : 'Cadastre a revista em Configuracoes Gerais.'}
                              </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <label className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={reuseSetting.includeTeachers}
                                  onChange={(event) =>
                                    updateClassReuseSetting(group.key, { includeTeachers: event.target.checked })
                                  }
                                />
                                Professores
                              </label>
                              <label className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={reuseSetting.includeStudents}
                                  onChange={(event) =>
                                    updateClassReuseSetting(group.key, { includeStudents: event.target.checked })
                                  }
                                />
                                Alunos
                              </label>
                            </div>

                            <div className="space-y-2">
                              <p className="text-xs font-medium text-gray-700">Turmas disponíveis:</p>
                              <div className="space-y-2">
                                {group.classes.map((classItem) => {
                                  const baseSelectedClassIds =
                                    formValues.classReuseMode === 'none'
                                      ? []
                                      : formValues.classReuseMode === 'all'
                                      ? group.classes.map((c) => c.id)
                                      : (reuseSetting.selectedClassIds ?? group.classes.map((c) => c.id));
                                  const isClassSelected = baseSelectedClassIds.includes(classItem.id);
                                  return (
                                    <label key={classItem.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                                      <input
                                        type="checkbox"
                                        checked={isClassSelected}
                                        onChange={(event) =>
                                          {
                                            const currentSelected = baseSelectedClassIds;
                                            const nextSelected = event.target.checked
                                              ? Array.from(new Set([...currentSelected, classItem.id]))
                                              : currentSelected.filter((id) => id !== classItem.id);

                                            setFormValues((current) => ({
                                              ...current,
                                              classReuseMode: 'selected',
                                            }));

                                            updateClassReuseSetting(group.key, {
                                              includeClass: nextSelected.length > 0,
                                              selectedClassIds: nextSelected,
                                            });
                                          }
                                        }
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-slate-900">{classItem.title}</p>
                                        <p className="text-xs text-gray-500">{classItem.students} alunos</p>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

                  {formValues.replicateFromId && isLoadingSourceClasses && (
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                        Carregando turmas do trimestre...
                      </div>
                    </div>
                  )}

                  {!formValues.replicateFromId && (
                    <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                      O trimestre sera criado sem reaproveitar turmas, alunos ou professores.
                    </p>
              )}
            </div>
          )}

          {formMode === 'create' && createStep === 3 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="shrink-0 text-emerald-700" />
                  <div>
                    <p className="text-sm font-bold text-emerald-900">Revisao antes de criar</p>
                    <p className="mt-1 text-sm text-emerald-800">
                      Confira o trimestre e os reaproveitamentos. A criacao acontece ao confirmar.
                    </p>
                  </div>
                </div>
              </div>

              <Card className="border-slate-200 p-4">
                <div className="space-y-2">
                  <p className="text-sm font-bold text-slate-900">
                    {formValues.number}º Trimestre {formValues.year}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatBrDate(formValues.startDate)} ate {formatBrDate(formValues.endDate)}
                  </p>
                </div>
              </Card>

              <div className="grid grid-cols-3 gap-2">
                <Card className="border-slate-200 p-3">
                  <UsersRound size={17} className="text-sky-700" />
                  <p className="mt-2 text-lg font-bold text-slate-900">{includedClassGroups.length}</p>
                  <p className="text-[11px] font-semibold text-gray-500">Turmas</p>
                </Card>
                <Card className="border-slate-200 p-3">
                  <UserMinus size={17} className="text-rose-700" />
                  <p className="mt-2 text-lg font-bold text-slate-900">{formValues.excludedStudentIds.length}</p>
                  <p className="text-[11px] font-semibold text-gray-500">Alunos fora</p>
                </Card>
                <Card className="border-slate-200 p-3">
                  <Layers3 size={17} className="text-emerald-700" />
                  <p className="mt-2 text-lg font-bold text-slate-900">
                    {
                      new Set(
                        includedClassGroups
                          .map((group) => classReuseSettings[group.key]?.segmentId)
                          .filter(Boolean)
                      ).size
                    }
                  </p>
                  <p className="text-[11px] font-semibold text-gray-500">Segmentos</p>
                </Card>
              </div>

              <div className="space-y-3">
                {includedClassGroups.map((group) => {
                  const reuseSetting = classReuseSettings[group.key];
                  const segment = segments.find((item) => item.id === reuseSetting?.segmentId);
                  const magazine = reuseSetting?.segmentId
                    ? getMagazineForSegment(reuseSetting.segmentId, formValues.replicateFromId)
                    : null;
                  const includedCount = reuseSetting?.includeStudents
                    ? group.classes.reduce((sum, classItem) => sum + classItem.students, 0)
                    : 0;

                  return (
                    <div key={group.key} className="rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-sm font-bold text-slate-900">{group.title}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {includedCount} alunos - {reuseSetting?.includeTeachers ? 'professores incluidos' : 'sem professores'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                          {segment?.name ?? 'Segmento'}
                        </span>
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                          {magazine?.title ?? 'Sem revista'}
                        </span>
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                          {magazine?.lessons.length ?? 0} lições
                        </span>
                      </div>
                    </div>
                  );
                })}

                {includedClassGroups.length === 0 && (
                  <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhuma turma sera reaproveitada neste trimestre.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={trimesterToDelete !== null}
        onClose={() => {
          if (!isSaving) {
            setTrimesterToDelete(null);
            setOperationError(null);
          }
        }}
        title="Remover trimestre"
        subtitle={trimesterToDelete?.label}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              disabled={isSaving}
              onClick={() => {
                setTrimesterToDelete(null);
                setOperationError(null);
              }}
            >
              Cancelar
            </Button>
            <Button className="h-12 bg-rose-600 hover:bg-rose-700" onClick={() => void handleConfirmDelete()} disabled={isSaving}>
              {isSaving ? 'Removendo...' : 'Remover trimestre'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {operationError && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700" />
              {operationError}
            </div>
          )}
          <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <AlertTriangle size={20} className="shrink-0 text-rose-600" />
            <p className="text-sm text-rose-900">
              Esta ação remove o trimestre e todos os vínculos de alunos armazenados nele. O histórico
              permanece disponível nos demais trimestres.
            </p>
          </div>
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={managedTrimester !== null}
        onClose={() => setManagedTrimesterId(null)}
        title={managedTrimester ? `Alunos do ${managedTrimester.label}` : 'Alunos'}
        subtitle="Remova alunos que não devem participar deste trimestre."
      >
        {managedGroups.length > 0 ? (
          <div className="space-y-4">
            {managedGroups.map((group) => (
              <div key={group.key} className="space-y-2">
                <p className="ml-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {group.title} • {group.students.length}
                </p>
                <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
                  {group.students.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center justify-between gap-3 px-3 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar
                          src={getPersonPhotoUrl(student.id)}
                          alt={`Foto de ${student.name}`}
                          name={student.name}
                          className="h-10 w-10 shrink-0 rounded-2xl ring-1 ring-slate-200"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">{student.name}</p>
                          <p className="text-xs text-gray-500">{student.ageGroup}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReplicatedStudent(managedTrimester!.id, student.id)}
                        className="flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700"
                      >
                        <UserMinus size={12} /> Remover
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Nenhum aluno vinculado a este trimestre.</p>
        )}
      </FullScreenModal>
    </MainLayout>
  );
};

export default TrimestersPage;
